import React, { useState, useMemo } from "react";
import { FORFETTARIO_THRESHOLD } from "../../constants/ateco";

const MONTH_LABELS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

// Altezza fissa del container delle barre in pixel
const CHART_HEIGHT = 280; // Aumentata per migliore visibilità

// Formattazione italiana: 1.234 € (solo migliaia, senza decimali)
function formatCurrency(value) {
  const num = Number(value || 0);
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  const intPart = Math.round(abs).toString();
  const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${groupedInt} €`;
}

function formatPercent(value) {
  if (!Number.isFinite(value)) return "—";
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(0)}%`;
}

export default function UnifiedChart({ invoices, selectedYear, soglia = FORFETTARIO_THRESHOLD, darkMode = false }) {
  const [viewMode, setViewMode] = useState("incassi"); // "incassi" | "fatturato"
  const [showPY2, setShowPY2] = useState(false);
  const [hoveredMonth, setHoveredMonth] = useState(null);

  // Calcola dati mensili basati su viewMode
  const monthlyData = useMemo(() => {
    const cy = Array(12).fill(0);
    const py = Array(12).fill(0);
    const py2 = Array(12).fill(0);

    invoices.forEach((inv) => {
      let dateToUse;

      if (viewMode === "incassi") {
        // Incassi: usa paymentDate per fatture pagate
        if (!inv.paid) return;
        dateToUse = inv.paymentDate || inv.issueDate;
      } else {
        // Fatturato: usa issueDate
        dateToUse = inv.issueDate;
      }

      if (!dateToUse) return;

      const date = new Date(dateToUse);
      const year = date.getFullYear();
      const month = date.getMonth(); // 0-11
      const amount = Number(inv.grossAmount) || 0;

      if (year === selectedYear) {
        cy[month] += amount;
      } else if (year === selectedYear - 1) {
        py[month] += amount;
      } else if (year === selectedYear - 2) {
        py2[month] += amount;
      }
    });

    return { cy, py, py2 };
  }, [invoices, selectedYear, viewMode]);

  const { cy, py, py2 } = monthlyData;

  // Calcola cumulativo per CY (per soglia 85k)
  const cumulativeCY = useMemo(() => {
    return cy.reduce((acc, val, idx) => {
      const prev = idx > 0 ? acc[idx - 1] : 0;
      acc.push(prev + val);
      return acc;
    }, []);
  }, [cy]);

  // Trova il mese in cui il cumulativo supera la soglia (ragguagliata nel primo anno)
  const thresholdMonth = cumulativeCY.findIndex((cum) => cum > soglia);

  // Calcola totali
  const totalCY = cy.reduce((a, b) => a + b, 0);
  const totalPY = py.reduce((a, b) => a + b, 0);
  const totalPY2 = py2.reduce((a, b) => a + b, 0);

  // Scala Y dinamica: massimo dei valori mensili * 1.2 per margine
  const maxValue = useMemo(() => {
    const allData = showPY2 ? [...cy, ...py, ...py2] : [...cy, ...py];
    const maxVal = Math.max(...allData);
    // Aggiungi 20% di margine, minimo 100 per evitare divisione per zero
    const calculatedMax = Math.max(maxVal * 1.2, 100);

    return calculatedMax;
  }, [cy, py, py2, showPY2]);

  // Altezza barra in PIXEL (non percentuale)
  const getBarHeight = (value) => {
    if (value <= 0) return 0;
    const height = (value / maxValue) * CHART_HEIGHT;
    return Math.max(height, 4); // Minimo 4px per visibilità
  };

  const getDeltaPct = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const totalDeltaPct = getDeltaPct(totalCY, totalPY);

  return (
    <div className="relative">
      {/* Toggle e controlli */}
      <div className="flex items-center justify-between mb-4">
        {/* Toggle Incassi/Fatturato */}
        <div className={`inline-flex rounded-lg border p-1 ${
          darkMode ? 'border-neutral-700 bg-neutral-800' : 'border-neutral-200 bg-neutral-100'
        }`}>
          <button
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === "incassi"
                ? darkMode
                  ? "bg-neutral-700 text-white shadow-sm"
                  : "bg-white text-neutral-900 shadow-sm"
                : darkMode
                  ? "text-neutral-500 hover:text-neutral-300"
                  : "text-neutral-600 hover:text-neutral-900"
            }`}
            onClick={() => setViewMode("incassi")}
          >
            Incassi
          </button>
          <button
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === "fatturato"
                ? darkMode
                  ? "bg-neutral-700 text-white shadow-sm"
                  : "bg-white text-neutral-900 shadow-sm"
                : darkMode
                  ? "text-neutral-500 hover:text-neutral-300"
                  : "text-neutral-600 hover:text-neutral-900"
            }`}
            onClick={() => setViewMode("fatturato")}
          >
            Fatturato
          </button>
        </div>

        {/* Pulsante +PY-2 */}
        <div className="flex items-center gap-2">
          <button
            className={`px-3 py-1.5 rounded-lg border text-sm ${
              !showPY2
                ? darkMode
                  ? "bg-neutral-800 border-neutral-700 hover:bg-neutral-700 text-neutral-400"
                  : "bg-white border-neutral-200 hover:bg-neutral-50 text-neutral-700"
                : darkMode
                  ? "bg-forfy-900 border-forfy-700 text-forfy-300"
                  : "bg-forfy-50 border-forfy-100 text-forfy-700"
            }`}
            onClick={() => setShowPY2(!showPY2)}
          >
            {showPY2 ? `− ${selectedYear - 2}` : `+ ${selectedYear - 2}`}
          </button>
        </div>
      </div>

      {/* Legenda */}
      <div className="flex items-center gap-6 mb-4 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-forfy-500"></div>
          <span className={darkMode ? 'text-neutral-500' : 'text-neutral-600'}>{selectedYear}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-neutral-400"></div>
          <span className={darkMode ? 'text-neutral-500' : 'text-neutral-600'}>{selectedYear - 1}</span>
        </div>
        {showPY2 && (
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-amber-400"></div>
            <span className={darkMode ? 'text-neutral-500' : 'text-neutral-600'}>{selectedYear - 2}</span>
          </div>
        )}
        {viewMode === "incassi" && (
          <div className="flex items-center gap-2 ml-auto">
            <div className="w-3 h-3 rounded bg-red-500"></div>
            <span className={darkMode ? 'text-neutral-500' : 'text-neutral-600'}>Cumulativo &gt; 85k</span>
          </div>
        )}
      </div>

      {/* Grafico */}
      <div className="relative flex gap-1 px-2">
        {MONTH_LABELS.map((label, idx) => {
          const cyVal = cy[idx];
          const pyVal = py[idx];
          const py2Val = py2[idx];
          const deltaPct = getDeltaPct(cyVal, pyVal);
          const cumulative = cumulativeCY[idx];
          const isOverThreshold = viewMode === "incassi" && thresholdMonth !== -1 && idx >= thresholdMonth;
          const isHovered = hoveredMonth === idx;

          return (
            <div
              key={label}
              className="flex-1 flex flex-col items-center relative"
              onMouseEnter={() => setHoveredMonth(idx)}
              onMouseLeave={() => setHoveredMonth(null)}
            >
              {/* Delta percentuale CY vs PY - altezza fissa */}
              <div className="h-5 flex items-center justify-center">
                {(cyVal > 0 || pyVal > 0) && (
                  <span
                    className={`text-[10px] font-medium ${
                      deltaPct >= 0 ? "text-emerald-600" : "text-red-500"
                    }`}
                  >
                    {formatPercent(deltaPct)}
                  </span>
                )}
              </div>

              {/* Container barre - ALTEZZA FISSA in pixel */}
              <div
                className="w-full flex items-end justify-center gap-0.5"
                style={{ height: `${CHART_HEIGHT}px` }}
              >
                {/* Barra PY-2 */}
                {showPY2 && (
                  <div
                    className="w-2 bg-amber-400 rounded-t transition-all"
                    style={{ height: `${getBarHeight(py2Val)}px` }}
                  />
                )}
                {/* Barra PY */}
                <div
                  className={`${showPY2 ? "w-2" : "w-3"} bg-neutral-400 rounded-t transition-all`}
                  style={{ height: `${getBarHeight(pyVal)}px` }}
                />
                {/* Barra CY - diventa rossa se cumulativo > 85k */}
                <div
                  className={`${showPY2 ? "w-2" : "w-3"} rounded-t transition-all ${
                    isOverThreshold ? "bg-red-500" : "bg-forfy-500"
                  }`}
                  style={{ height: `${getBarHeight(cyVal)}px` }}
                />
              </div>

              {/* Label mese - altezza fissa */}
              <div className={`h-5 flex items-center justify-center text-[10px] ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                {label}
              </div>

              {/* Tooltip */}
              {isHovered && (cyVal > 0 || pyVal > 0 || (showPY2 && py2Val > 0)) && (
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-20 bg-neutral-900 text-white text-xs rounded-lg p-2 whitespace-nowrap shadow-lg">
                  <div className="font-medium mb-1">{MONTH_LABELS[idx]} {selectedYear}</div>
                  <div className="flex justify-between gap-4">
                    <span className="text-forfy-300">{selectedYear}:</span>
                    <span>{formatCurrency(cyVal)}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-neutral-400">{selectedYear - 1}:</span>
                    <span>{formatCurrency(pyVal)}</span>
                  </div>
                  {showPY2 && (
                    <div className="flex justify-between gap-4">
                      <span className="text-amber-300">{selectedYear - 2}:</span>
                      <span>{formatCurrency(py2Val)}</span>
                    </div>
                  )}
                  {viewMode === "incassi" && (
                    <div className="flex justify-between gap-4 border-t border-neutral-700 mt-1 pt-1">
                      <span className="text-neutral-400">Cumul.:</span>
                      <span className={cumulative > soglia ? "text-red-400" : ""}>
                        {formatCurrency(cumulative)}
                      </span>
                    </div>
                  )}
                  {viewMode === "incassi" && cumulative > soglia && (
                    <div className="text-red-400 text-[10px] mt-1">Soglia superata!</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Riepilogo sotto il grafico */}
      <div className={`mt-4 pt-4 border-t grid gap-4 text-center ${
        darkMode ? 'border-neutral-800' : 'border-neutral-200'
      } ${showPY2 ? "grid-cols-4" : "grid-cols-3"}`}>
        <div>
          <div className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>{selectedYear}</div>
          <div className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
            {formatCurrency(totalCY)}
          </div>
        </div>
        <div>
          <div className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>{selectedYear - 1}</div>
          <div className={`text-lg font-semibold ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
            {formatCurrency(totalPY)}
          </div>
        </div>
        {showPY2 && (
          <div>
            <div className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>{selectedYear - 2}</div>
            <div className={`text-lg font-semibold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
              {formatCurrency(totalPY2)}
            </div>
          </div>
        )}
        <div>
          <div className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>Δ CY/PY</div>
          <div
            className={`text-lg font-semibold ${
              totalDeltaPct >= 0
                ? darkMode ? "text-emerald-400" : "text-emerald-600"
                : darkMode ? "text-red-400" : "text-red-600"
            }`}
          >
            {formatPercent(totalDeltaPct)}
          </div>
        </div>
      </div>

      {/* Info sulla vista corrente */}
      <div className={`mt-3 text-xs text-center ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
        {viewMode === "incassi"
          ? "Basato sulla data di incasso (paymentDate) — Le barre rosse indicano superamento cumulativo 85k"
          : "Basato sulla data di emissione (issueDate)"
        }
      </div>
    </div>
  );
}
